import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Store, User, MapPin, Clock, Save, Navigation, Wand2, CalendarOff } from "lucide-react";
import { HolidayManager } from "@/components/HolidayManager";
import { useQueryClient } from "@tanstack/react-query";
import LocationPicker, { reverseGeocode } from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import { updateStore, errMsg, getOutlet, saveOutletAddress, getBusinessHours, saveBusinessHours } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { cn } from "@/lib/utils";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const defaultTimings = () =>
  DAYS.map((d) => ({ day: d.toLowerCase(), open: true, from: "09:00", to: "22:00" }));
const normHours = (hours) =>
  DAYS.map((d) => {
    const h = (hours || []).find((x) => String(x.day).toLowerCase() === d.toLowerCase());
    return h
      ? { day: d.toLowerCase(), open: !!h.open, from: h.from || "09:00", to: h.to || "22:00" }
      : { day: d.toLowerCase(), open: true, from: "09:00", to: "22:00" };
  });
const detailMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join("; ");
  if (e?.response?.status === 404) return "Business hours service isn't live on the server yet";
  return errMsg(e);
};

const Panel = ({ title, icon: Icon, children, footer }) => (
  <div className="rounded-md border border-border bg-card">
    <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border">
      <Icon className="h-4 w-4 text-primary" />
      <h3 className="font-display font-bold text-lg">{title}</h3>
    </div>
    <div className="p-5 space-y-4">{children}</div>
    {footer && <div className="px-5 py-4 border-t border-border">{footer}</div>}
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">{label}</p>
    <p className="font-medium mt-0.5">{value || "—"}</p>
  </div>
);

export default function Outlet() {
  const { vendor, refreshVendor, patchVendorLocal } = useAuth();
  const [shopName, setShopName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [timings, setTimings] = useState(defaultTimings());
  const [hoursDefault, setHoursDefault] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [loc, setLoc] = useState({ address: "", lat: "", lng: "", city: "", pincode: "" });
  const [savingLoc, setSavingLoc] = useState(false);
  const [filling, setFilling] = useState(false);
  const qc = useQueryClient();

  const fromServer = (data) => ({
    address: data?.address || "",
    lat: data?.latitude != null ? String(data.latitude) : "",
    lng: data?.longitude != null ? String(data.longitude) : "",
    city: data?.city || "",
    pincode: data?.pincode || "",
  });

  useEffect(() => {
    setShopName(vendor?.shop_name || "");
  }, [vendor?.shop_name]);

  useEffect(() => {
    if (!vendor) return;
    getBusinessHours()
      .then(({ data }) => {
        setTimings(normHours(data?.hours));
        setHoursDefault(!!data?.is_default);
      })
      .catch(() => {});
    getOutlet()
      .then(({ data }) => setLoc(fromServer(data)))
      .catch(() => {});
  }, [vendor]);

  const saveName = async () => {
    if (!shopName.trim()) return toast.error("Shop name can't be empty");
    setSavingName(true);
    try {
      await updateStore({ shop_name: shopName.trim() });
      patchVendorLocal({ shop_name: shopName.trim() });
      await refreshVendor();
      toast.success("Shop name updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingName(false);
    }
  };

  const setDay = (i, patch) => setTimings((t) => t.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const saveTimings = async () => {
    const bad = timings.find((d) => d.open && (!d.from || !d.to));
    if (bad) return toast.error(`Set opening and closing time for ${bad.day}`);
    setSavingHours(true);
    try {
      const { data } = await saveBusinessHours({ hours: timings });
      if (data?.hours) setTimings(normHours(data.hours));
      setHoursDefault(false);
      toast.success("Business hours saved");
      qc.invalidateQueries({ queryKey: ["business-hours"] });
    } catch (e) {
      toast.error(detailMsg(e));
    } finally {
      setSavingHours(false);
    }
  };

  const saveLoc = async () => {
    const address = loc.address.trim();
    if (!address) return toast.error("Full address can't be empty");
    if (address.length > 400) return toast.error("Address is too long (max 400 characters)");
    const latitude = parseFloat(loc.lat);
    const longitude = parseFloat(loc.lng);
    if (Number.isNaN(latitude) || latitude < -90 || latitude > 90)
      return toast.error("Latitude must be a number between -90 and 90");
    if (Number.isNaN(longitude) || longitude < -180 || longitude > 180)
      return toast.error("Longitude must be a number between -180 and 180");
    const city = loc.city.trim();
    const pincode = loc.pincode.trim();
    if (!city) return toast.error("City can't be empty");
    if (!/^[1-9][0-9]{5}$/.test(pincode)) return toast.error("Pincode must be a valid 6-digit number");
    setSavingLoc(true);
    try {
      await saveOutletAddress({ address, latitude, longitude, city, pincode });
      const { data } = await getOutlet();
      setLoc(fromServer(data));
      qc.invalidateQueries({ queryKey: ["outlet"] });
      const cityOk = (data?.city || "").toLowerCase() === city.toLowerCase() && String(data?.pincode || "") === pincode;
      if (cityOk) toast.success("Address saved — synced to the mobile app");
      else toast.warning("Address & map pin saved. City/pincode were not accepted by the server yet — your backend team needs to enable them on PUT /vendor/outlet/address.", { duration: 8000 });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingLoc(false);
    }
  };

  const setPin = (a, b) => setLoc((l) => ({ ...l, lat: a.toFixed(6), lng: b.toFixed(6) }));

  const fillFromPin = async () => {
    const a = parseFloat(loc.lat);
    const b = parseFloat(loc.lng);
    if (Number.isNaN(a) || Number.isNaN(b)) return toast.error("Set the map pin first");
    setFilling(true);
    try {
      const r = await reverseGeocode(a, b);
      const ad = r?.address || {};
      setLoc((l) => ({
        ...l,
        address: l.address.trim() ? l.address : r?.display_name || "",
        city: ad.city || ad.town || ad.village || ad.county || l.city,
        pincode: ad.postcode ? String(ad.postcode).replace(/\s/g, "") : l.pincode,
      }));
      toast.success("City & pincode filled from the pin — please review");
    } catch {
      toast.error("Couldn't look up this location");
    } finally {
      setFilling(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoc((l) => ({ ...l, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }));
        toast.success("Location captured");
      },
      () => toast.error("Couldn't get location")
    );
  };

  return (
    <div className="p-5 sm:p-8 max-w-5xl">
      <PageHeader title="Outlet" subtitle="Your shop profile, hours & location" />

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel
          title="Shop profile"
          icon={Store}
          footer={
            <Button onClick={saveName} disabled={savingName} data-testid="save-shopname-btn" className="font-semibold">
              {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save name</>)}
            </Button>
          }
        >
          <div className="space-y-1.5">
            <Label>Shop name</Label>
            <Input data-testid="outlet-shopname" value={shopName} onChange={(e) => setShopName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4 pt-1">
            <ReadOnly label="Owner" value={vendor?.owner_name} />
            <ReadOnly label="Category" value={vendor?.category} />
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
            <User className="h-3 w-3" /> Owner &amp; category are managed by 1APPONLY admin.
          </p>
        </Panel>

        <div className="lg:col-span-2">
        <Panel
          title="Address & location"
          icon={MapPin}
          footer={
            <Button onClick={saveLoc} disabled={savingLoc} data-testid="save-location-btn" className="font-semibold">
              {savingLoc ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save address</>)}
            </Button>
          }
        >
          <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Full address</Label>
            <Textarea data-testid="outlet-address" rows={3} value={loc.address} onChange={(e) => setLoc({ ...loc, address: e.target.value })} placeholder="Shop no, street, area, landmark" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input data-testid="outlet-city" value={loc.city} onChange={(e) => setLoc({ ...loc, city: e.target.value })} placeholder="e.g. Repalle" />
            </div>
            <div className="space-y-1.5">
              <Label>Pincode</Label>
              <Input data-testid="outlet-pincode" inputMode="numeric" maxLength={6} value={loc.pincode} onChange={(e) => setLoc({ ...loc, pincode: e.target.value.replace(/\D/g, "") })} placeholder="6 digits" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Latitude</Label>
              <Input data-testid="outlet-lat" value={loc.lat} onChange={(e) => setLoc({ ...loc, lat: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Longitude</Label>
              <Input data-testid="outlet-lng" value={loc.lng} onChange={(e) => setLoc({ ...loc, lng: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            <Button variant="ghost" size="sm" onClick={useMyLocation} data-testid="use-location-btn" className="text-primary">
              <Navigation className="h-4 w-4 mr-1.5" /> Use my current location
            </Button>
            <Button variant="ghost" size="sm" onClick={fillFromPin} disabled={filling} data-testid="fill-from-pin-btn" className="text-primary">
              {filling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Fill city &amp; pincode from pin
            </Button>
          </div>
          </div>
          <LocationPicker lat={parseFloat(loc.lat)} lng={parseFloat(loc.lng)} onChange={setPin} />
          </div>
        </Panel>
        </div>
      </div>

      <div className="mt-4">
        <Panel
          title="Business hours"
          icon={Clock}
          footer={
            <Button onClick={saveTimings} disabled={savingHours} data-testid="save-timings-btn" className="font-semibold">
              {savingHours ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save hours</>)}
            </Button>
          }
        >
          {hoursDefault && (
            <p data-testid="hours-default-note" className="text-xs text-muted-foreground">
              Showing default hours — save to set your own. Changes sync to the mobile app.
            </p>
          )}
          <div className="divide-y divide-border">
            {timings.map((d, i) => (
              <div key={d.day} className="flex items-center gap-3 py-2.5" data-testid={`timing-row-${i}`}>
                <span className="w-24 font-medium text-sm">{DAYS[i]}</span>
                <Switch checked={d.open} onCheckedChange={(v) => setDay(i, { open: v })} data-testid={`timing-open-${i}`} />
                {!d.open ? (
                  <span className="text-sm text-muted-foreground">Closed</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input type="time" value={d.from} onChange={(e) => setDay(i, { from: e.target.value })} className="w-32 h-9" data-testid={`timing-from-${i}`} />
                    <span className="text-muted-foreground text-sm">to</span>
                    <Input type="time" value={d.to} onChange={(e) => setDay(i, { to: e.target.value })} className="w-32 h-9" data-testid={`timing-to-${i}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="mt-4">
        <Panel title="Holiday closures" icon={CalendarOff}>
          <p className="text-xs text-muted-foreground -mt-1">
            Close on specific dates (festivals, leave) without touching your weekly hours.
          </p>
          <HolidayManager />
        </Panel>
      </div>
    </div>
  );
}
